import { TRACKERS } from "@/constants/Trackers";
import { trackEvent } from "@/lib/tracking";
import { useUser } from "@/network/useUser";
import { socketsAtom } from "@/store/socketsAtom";
import { useAtomValue } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { ChatActionType } from "../network/useChatAvailableActions";
import {
  AsyncMessageTypes,
  MessageType,
  VIDEO_CALL_EVENT,
} from "../types/chatMessageTypes";

interface SendMessageParams {
  text: string;
  roomToken: string;
}

interface SendVideoMessageParams {
  roomToken: string;
  videoCallEvent: (typeof VIDEO_CALL_EVENT)[keyof typeof VIDEO_CALL_EVENT];
}

enum ChatMode {
  ASYNC = "async_message",
  LIVE = "chat_message",
}

export function useSendChatMessage({
  chatMode,
}: {
  chatMode?: ChatActionType;
}) {
  const typingTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isAsync = chatMode === ChatActionType.ASYNC_CHAT;
  const messageType =
    chatMode === ChatActionType.ASYNC_CHAT
      ? ChatMode.ASYNC
      : chatMode === ChatActionType.LIVE_CHAT
        ? ChatMode.LIVE
        : undefined;
  const { data: user } = useUser();

  // Get socket directly from the atom without setting up listeners
  const socket = useAtomValue(socketsAtom)?.chat;

  // Clean up typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const sendTypingIndicator = useCallback(
    (roomToken: string, isTyping: boolean) => {
      if (!socket || isAsync || !messageType) return;

      socket.emit(messageType, {
        type: isTyping ? MessageType.START_TYPING : MessageType.STOP_TYPING,
        room_token: roomToken,
      });
    },
    [socket, messageType, isAsync],
  );

  const handleTyping = useCallback(
    (roomToken: string) => {
      if (!socket || isAsync || !messageType) return;

      // Send typing indicator
      sendTypingIndicator(roomToken, true);

      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Set new timeout to stop typing after 1 second of no typing
      typingTimeoutRef.current = setTimeout(() => {
        sendTypingIndicator(roomToken, false);
      }, 1000);
    },
    [sendTypingIndicator, socket, isAsync, messageType],
  );

  const sendMessage = useCallback(
    ({ text, roomToken }: SendMessageParams) => {
      if (!socket || !messageType) return;
      if (isAsync) {
        trackEvent(TRACKERS.SEND_ASYNC_MESSAGE);
      } else {
        trackEvent(TRACKERS.SEND_CHAT_MESSAGE);
      }

      // Clear any existing typing timeout and send stop typing
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      sendTypingIndicator(roomToken, false);

      // Generate a client UUID for this message
      const clientUuid = uuidv4();

      // Send the message
      socket.emit(messageType, {
        type: isAsync ? AsyncMessageTypes.TEXT : MessageType.TEXT,
        room_token: roomToken,
        text,
        client_uuid: clientUuid,
        model_type: isAsync ? "AsyncMessage" : "Message",
      });
    },
    [socket, messageType, isAsync, sendTypingIndicator],
  );

  const sendVideoMessage = useCallback(
    ({ roomToken, videoCallEvent }: SendVideoMessageParams) => {
      if (!socket || !messageType) {
        console.warn("socket is not connected");
        return;
      }
      const message = {
        user_id: user?.userId,
        type: MessageType.VIDEO_CALL,
        room_token: roomToken,
        video_call_event: videoCallEvent,
        final: videoCallEvent !== VIDEO_CALL_EVENT.PROVIDER_JOINED,
        practitioner_name: user?.professional.displayNameInternal,
      };

      socket.emit(messageType, message);
    },
    [socket, messageType, user?.userId, user?.professional.displayNameInternal],
  );

  return {
    handleTyping,
    sendMessage,
    sendVideoMessage,
  };
}
